import Grid from '@mui/material/Grid2'
import {
	TextField,
	Select,
	MenuItem,
	Button,
	FormControl,
	InputLabel,
	ListSubheader,
	IconButton,
	Box,
	Typography
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useCrop } from '../hooks/useCrop'
import { useCategory } from '../hooks/useCategory'

interface BatchSearchFilterProps {
	searchTerm: string
	filterType: string
	sortBy: string
	sortOrder: 'asc' | 'desc'
	onSearchTermChange: (value: string) => void
	onFilterTypeChange: (value: string) => void
	onSortByChange: (value: string) => void
	onSortOrderToggle: () => void
	onSearch: () => void
}

export const BatchSearchFilter = ({
	searchTerm,
	filterType,
	sortBy,
	sortOrder,
	onSearchTermChange,
	onFilterTypeChange,
	onSortByChange,
	onSortOrderToggle,
	onSearch
}: BatchSearchFilterProps) => {
	const { crops, loadingList: loadingCrops } = useCrop()
	const { categories, loadingList: loadingCategories } = useCategory()

	const renderSearchField = () => {
		switch (filterType) {
			case 'crop':
				return (
					<FormControl fullWidth>
						<InputLabel>Seleccionar Cultivo</InputLabel>
						<Select
							value={searchTerm}
							onChange={(e) => onSearchTermChange(e.target.value)}
							label="Seleccionar Cultivo"
							disabled={loadingCrops}
						>
							{crops.map((crop) => (
								<MenuItem key={crop.uid} value={crop.name}>
									{crop.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)
			case 'germplasm':
				return (
					<FormControl fullWidth>
						<InputLabel>Seleccionar Germoplasma</InputLabel>
						<Select
							value={searchTerm}
							onChange={(e) => onSearchTermChange(e.target.value)}
							label="Seleccionar Germoplasma"
							disabled={loadingCrops}
						>
							{crops.map((crop) => [
								<ListSubheader key={`header-${crop.uid}`} sx={{ backgroundColor: '#f5f5f5' }}>
									{crop.name}
								</ListSubheader>,
								...crop.germplasm.map((germ) => (
									<MenuItem key={`${crop.uid}-${germ.name}`} value={germ.name} sx={{ pl: 4 }}>
										{germ.name}
									</MenuItem>
								))
							])}
						</Select>
					</FormControl>
				)
			case 'category':
				return (
					<FormControl fullWidth>
						<InputLabel>Seleccionar Categoría</InputLabel>
						<Select
							value={searchTerm}
							onChange={(e) => onSearchTermChange(e.target.value)}
							label="Seleccionar Categoría"
							disabled={loadingCategories}
						>
							{categories.map((category) => (
								<MenuItem key={category.uid} value={category.name}>
									{category.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)
			default:
				return (
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Buscar lotes..."
						value={searchTerm}
						onChange={(e) => onSearchTermChange(e.target.value)}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								onSearch();
							}
						}}
					/>
				)
		}
	}

	return (
		<Grid container spacing={2} mb={3}>
			<Grid size={{ xs: 12, sm: 6 }}>
				{renderSearchField()}
			</Grid>
			<Grid size={{ xs: 12, sm: 3 }}>
				<FormControl fullWidth>
					<InputLabel>Filtrar por</InputLabel>
					<Select
						value={filterType}
						onChange={(e) => {
							onFilterTypeChange(e.target.value)
							onSearchTermChange('') // Resetear el término de búsqueda al cambiar el tipo
						}}
						label="Filtrar por"
					>
						<MenuItem value="batchId">No. Lote</MenuItem>
						<MenuItem value="crop">Cultivo</MenuItem>
						<MenuItem value="germplasm">Germoplasma</MenuItem>
						<MenuItem value="category">Categoría</MenuItem>
					</Select>
				</FormControl>
			</Grid>
			<Grid size={{ xs: 12, sm: 3 }}>
				<Box display="flex" gap={1}>
					<FormControl fullWidth>
						<InputLabel>Ordenar por</InputLabel>
						<Select
							value={sortBy}
							onChange={(e) => onSortByChange(e.target.value)}
							label="Ordenar por"
						>
							<MenuItem value="date">Fecha de Ingreso</MenuItem>
							<MenuItem value="crop">Cultivo</MenuItem>
							<MenuItem value="germplasm">Germoplasma</MenuItem>
							<MenuItem value="category">Categoría</MenuItem>
						</Select>
					</FormControl>
					<IconButton
						onClick={onSortOrderToggle}
						color="primary"
						sx={{
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 1,
							height: 56
						}}
						title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
					>
						{sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
					</IconButton>
				</Box>
			</Grid>
		</Grid>
	)
}
